import React, { useCallback, useEffect, useRef, useState, } from 'react';
import classNames from 'classnames';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
/*
 * Bronson InlineEditable component.
 */
export const InlineEditable = ({ ariaLabel, className, defaultActive, editLabel = 'Edit value', errorMessage, inputProps, onReset, onSubmit, resetLabel = 'Reset value', submitLabel = 'Submit value', submittedValue, valid, value, ...otherProps }) => {
    /**
     * Get ref for input element.
     */
    const inputRef = useRef(null);
    /**
     * Manage active state to show editable mode.
     */
    const [isActive, setIsActive] = useState(defaultActive);
    /**
     * Keep track of initialValue for comparison and reset.
     */
    const [initialValue, setInitialValue] = useState(value ?? '');
    /**
     * Keep track of current value inside input
     */
    const [currentValue, setCurrentValue] = useState(value ?? '');
    /**
     * Sync currentValue and initialValue with external value prop
     */
    useEffect(() => {
        if (value !== undefined) {
            setCurrentValue(value);
            setInitialValue(value);
            if (inputRef.current) {
                inputRef.current.parentElement?.setAttribute('data-inline-editable-size', value.replaceAll(' ', '\u00a0'));
            }
        }
    }, [value]);
    /**
     * Set the {isActive} state to true and memoize the function.
     * @type {(function(): void)|*}
     */
    const activate = useCallback(() => {
        setIsActive(true);
        inputRef?.current?.focus();
    }, []);
    /**
     * Set the {isActive} state to false and memoize the function.
     * @type {(function(): void)|*}
     */
    const deactivate = useCallback(() => {
        setIsActive(false);
        inputRef?.current?.blur();
    }, []);
    /**
     * Handle key presses when inactive.
     * If user presses Enter → activate.
     * If user presses Tab → blur input.
     * If tries typing → block unless active.
     */
    const handleKeyDown = (event) => {
        if (!isActive) {
            if (event.key === 'Enter') {
                event.preventDefault();
                setIsActive(true);
                inputRef.current?.focus();
            }
            else if (event.key === 'Tab') {
                inputRef.current?.blur();
            }
            else {
                event.preventDefault();
            }
        }
    };
    /**
     * Handle value changes.
     * Remove the disabled state if the initial value differs from the input.
     * Also dynamically set size for visual fit.
     */
    const handleChange = (e) => {
        const val = e.target.value || '';
        setCurrentValue(val);
        e.target.parentElement?.setAttribute('data-inline-editable-size', val.replaceAll(' ', '\u00a0'));
        otherProps.onChange?.(e);
    };
    /**
     * Handle the form submit.
     * - Prevent default submit.
     * - Update initialValue.
     * - Disable submit button.
     * - Blur input.
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        deactivate();
        setInitialValue(currentValue);
        if (inputRef.current) {
            inputRef.current.defaultValue = currentValue;
        }
        onSubmit?.(e);
    };
    /**
     * Handle the form reset.
     * - Restore initialValue.
     * - Remove active state.
     */
    const handleReset = (e) => {
        deactivate();
        setCurrentValue(initialValue);
        if (inputRef.current) {
            inputRef.current.value = initialValue;
            inputRef.current.parentElement?.setAttribute('data-inline-editable-size', initialValue.replaceAll(' ', '\u00a0'));
        }
        onReset?.(e);
    };
    /**
     * The InlineEditable class names.
     * @type {string}
     */
    const inlineEditableClassNameList = classNames('c-inline-editable', { 'is-active': isActive }, className).trim();
    /**
     * Edit Button
     * @type {JSX.Element}
     */
    const editButton = (React.createElement(Button, { link: true, simple: true, small: true, hiddenLabel: true, ariaLabel: editLabel, className: "c-inline-editable__btn", type: "button", icon: "semantic-edit", "data-tr-component": "Button", "data-tr-function": "Edit inline value", onClick: activate }));
    /**
     * Check if the submit button should be disabled
     */
    const isSubmitDisabled = () => {
        if (submittedValue) {
            if (currentValue === submittedValue)
                return true;
        }
        else {
            if (currentValue === initialValue)
                return true;
        }
        if (valid === false)
            return true;
        return false;
    };
    /**
     * Submit Button
     * @type {JSX.Element}
     */
    const submitButton = (React.createElement(Button, { hiddenLabel: true, className: "c-inline-editable__submit", type: "submit", icon: "semantic-checkmark", "data-tr-component": "Button", ariaLabel: submitLabel, "data-tr-function": "Submit inline value", disabled: isSubmitDisabled() }));
    /**
     * Reset Button
     * @type {JSX.Element}
     */
    const resetButton = (React.createElement(Button, { secondary: true, hiddenLabel: true, type: "reset", ariaLabel: resetLabel, className: "c-inline-editable__cancel", icon: "semantic-crossed-out", "data-tr-component": "Button", "data-tr-function": "Reset inline value" }));
    return (React.createElement("form", { "aria-label": ariaLabel, className: inlineEditableClassNameList, onReset: handleReset, onSubmit: handleSubmit },
        React.createElement("div", { className: "c-inline-editable__wrap" },
            React.createElement(Input, { modifierStates: "c-inline-editable__input", addonClassName: "c-inline-editable__input-addon c-input__addon--no-background", onClick: activate, onKeyDown: handleKeyDown, onChange: handleChange, size: 1, divProps: { 'data-inline-editable-size': currentValue || 1 }, ...otherProps, value: currentValue, ref: inputRef, ...inputProps }),
            editButton,
            submitButton,
            resetButton),
        valid ? null : errorMessage));
};
